<template>
    <button v-if="monthlyPayment"
        type="button"
        class="text-sm tracking-xl hover:underline focus:underline"
        @click="isOpen = true"
        @keypress="isOpen = true">
        <div class="flex flex-row items-center">
            <v-icon icon="empty-wallet-change"
                class="mr-xs text-lg" />

            <p>{{ t('product-part-payment') }} &nbsp; </p>

            <b>
                {{ t('product-part-payment-prefix') }}
                {{ formatCurrency(monthlyPayment) }}
                {{ t('product-part-payment-suffix') }}
            </b>
        </div>
    </button>

    <v-modal :is-open="isOpen"
        class="bg-white"
        @close="isOpen = false">
        <v-modal-header @close="isOpen = false">
            {{ content?.content?.title }}
        </v-modal-header>

        <div class="space-y-lg px-lg pb-20">
            <sb-rich-text :document="content?.content?.ingress" />

            <v-avarda v-if="currentLang === 'fi'"
                :price="priceForCalculator" />

            <!-- eslint-disable max-len -->
            <div v-if="currentLang === 'et'">
                <iframe scrolling="no"
                    title="Inbank osamakse kalkulaator"
                    :src="`https://calculator.inbank.ee/calculator/indivy/plan?amount=${priceForCalculator}&shop_uuid=350b5ed9-8164-4c2c-bc69-39c63632bd7b&product_code=hp_epos_masku_99&currency=eur&lang=et&template=editable_amount&mode=grey&logo=true&show_sample=false&start_session=false&secondary_color=red`"
                    width="400"
                    height="550"
                    frameborder="0"
                    class="w-full">
                </iframe>
            </div>
            <!-- eslint-enable max-len -->

            <v-disclosure v-for="item in installments"
                :key="item._uid"
                :is-open="item.is_open">
                <template #button>
                    <p class="font-bold">{{ item.title }}</p>
                </template>

                <template #content>
                    <h-switch-group class="prose space-y-md"
                        as="div">
                        <sb-rich-text :document="item.content" />
                    </h-switch-group>
                </template>
            </v-disclosure>
        </div>
    </v-modal>
</template>

<script lang="ts" setup>
const properties = defineProps<{
    price: string
}>();

const { $i18n } = useNuxtApp();
const { format: formatCurrency } = useCurrency();

const currentLang = computed(() => $i18n.locale.value);

const { t } = useI18n();

const priceForCalculator = computed(() => (
    properties.price
        ? Number.parseInt(properties.price, 10)
        : undefined
));

const isOpen = ref(false);

function getMonthlyPayment(
    price: number,
    months: number,
    statementFee: number,
    yearInterestRate: number,
): number {
    const value = Math.round(
        (
            (
                (yearInterestRate / 1200)
                + ((yearInterestRate / 1200) / (((1 + (yearInterestRate / 1200)) ** months) - 1))
            )
            * price * 100
        ) / 100,
    ) + statementFee;
    return value < 8 ? 8 : value;
}

const monthlyPayment = computed<number | undefined>(() => (
    properties.price
        ? getMonthlyPayment(
            Number.parseInt(properties.price, 10),
            36,
            3.95,
            19,
        )
        : undefined
));

const content = useStoryblokStory('global/installment-options');

const installments = computed(() => content.value?.content?.installments || []);
</script>

<i18n lang="json">
{
    "et": {
        "product-part-payment": "Maksa osade kaupa ",
        "product-part-payment-prefix": "al.",
        "product-part-payment-suffix": "/kuu"
    },
    "fi": {
        "product-part-payment": "Maksa erissä",
        "product-part-payment-prefix": "alk.",
        "product-part-payment-suffix": "/kk"
    }
}
</i18n>
